<template>
  <time
    :datetime="time"
    :title="localeDateString"
    :class="{ warning: relativeTime.direction === 'time.in_future' }"
  >
    <template
      v-if="isSwatchTime"
    >
      {{ swatchTime }}
    </template>
    <template
      v-else-if="withDirection"
    >
      {{
        relativeTime.direction === '' ?
          $tc(relativeTime.key, relativeTime.num, [relativeTime.num]) :
          $t(relativeTime.direction, [$tc(relativeTime.key, relativeTime.num, [relativeTime.num])])
      }}
    </template>
    <template v-else>
      {{ $tc(relativeTime.key, relativeTime.num, [relativeTime.num]) }}
    </template>
  </time>
</template>

<script>
import * as DateUtils from 'src/services/date_utils/date_utils.js'
import localeService from 'src/services/locale/locale.service.js'

export default {
  name: 'Timeago',
  props: ['time', 'autoUpdate', 'longFormat', 'nowThreshold', 'withDirection'],
  data () {
    return {
      relativeTime: { key: 'time.now', num: 0 },
      interval: null,
    }
  },
  computed: {
    localeDateString () {
      const browserLocale = localeService.internalToBrowserLocale(this.$i18n.locale)
      return typeof this.time === 'string'
        ? new Date(Date.parse(this.time)).toLocaleString(browserLocale)
        : this.time.toLocaleString(browserLocale)
    },
    isSwatchTime () {
      return this.$store.getters.mergedConfig.timeType === 'beats'
    },
    swatchTime () {
      // We parse the date into a Date object, if it's a string (it is sometimes.)
      let postTime = typeof this.time === 'string'
        ? new Date(Date.parse(this.time)) : this.time

      // We must create a clone of the post time, so we can manipulate it.
      // If we don't explicitly create a new Date, it will reference the original
      // variable instead, including all manipulations we do to that date to derive
      // the swatch time.
      let workingTime = new Date(postTime.getTime());

      // Before we do ANY manipulations to the time, parse out month, day, and year.
      // We specify a time zone of +1, since Swatch time is UTC+1.
      // We do this BEFORE modifying the time we're working with to UTC+1, because
      // it appears toLocaleDateString expects the Time object to be in UTC.
      const browserLocale = localeService.internalToBrowserLocale(this.$i18n.locale)
      const dateOptions = {
        timeZone: "+01:00",
        month: "short",
        day: "numeric",
        year: "numeric"
      }
      let postDate = workingTime.toLocaleDateString(browserLocale, dateOptions)

      // Now that we've set the date string, we can now work on manipulating our
      // time variable so it's reflective of Swatch time. Since our epoch time is
      // UTC and Swatch time is UTC+1, add an hour's worth of milliseconds.
      workingTime.setTime(workingTime.getTime() + (1*60*60*1000))

      // This is a bit of a one-liner, so let's walk through it.
      // We start by returning the date string we came up with earlier, followed by @.
      // The follow it up with our swatch time - but before we can return that, we have to do a bit of math.

      // What we have is the number of milliseconds that have passed since midnight, Jan 1st, 1970, in UTC+1.
      // What we want is the beats that have passed in the current day - that is, how many 1/1000ths of the day have passed.

      // We obtain the number of milliseconds that have passed just today, by dividing the number of milliseconds in a day
      // and seeing what the remainder is. (This is known as a modulus operation.)

      // We then divide our result by the number of milliseconds in a beat (1/1000ths of a day.)
      // (Funnily enough, the number of milliseconds in a beat is the same as number of seconds in a day.)
      // Finally, we round the resulting number to 2 decimal places, and we have our beat time.

      return postDate + " @" + Math.abs((workingTime.getTime() % (24*60*60*1000))/(24*60*60)).toFixed(2)
    }
  },
  created () {
    this.refreshRelativeTimeObject()
  },
  unmounted () {
    clearTimeout(this.interval)
  },
  methods: {
    refreshRelativeTimeObject () {
      const nowThreshold = typeof this.nowThreshold === 'number' ? this.nowThreshold : 1
      this.relativeTime = this.longFormat
        ? DateUtils.relativeTime(this.time, nowThreshold)
        : DateUtils.relativeTimeShort(this.time, nowThreshold)
      if (this.autoUpdate) {
        this.interval = setTimeout(
          this.refreshRelativeTimeObject,
          1000 * this.autoUpdate
        )
      }
    }
  }
}
</script>

<style lang="scss">
@import '../../_variables.scss';
.timeago {
  time.warning {
    color: var(--alertWarning, $fallback--alertWarning);
  }
}
</style>
